/**
 * egitimTuru.js — firma portalının kullanım turu: hangi adımlar, kime, ne zaman (2026-10-28; Bilge'nin "Tutorial Özeti" tablosuna göre
 * genişletildi, 2026-10-30; bağlama dayalı hâle getirildi, 2026-10-30).
 *
 * Tur yalnızca KATILIMCI setindeki bir firma girişi içindir (Yerli ve Yabancı), personelin "portalı bu firma olarak önizle"si
 * için değil. Tur sabit bir sıra değildir: ekranda ne açıksa onun anlatımıdır. Her BAĞLAM (bir sayfa, Firma Kartı'nın bir sekmesi,
 * onay ekranı, açık bir pencere) kendi küçük anlatımına sahiptir; `baglamBul` ekran durumundan (açık pencere, onay geçidi, adres,
 * etkin sekme) hangi bağlamda olunduğunu bulur, `egitimAdimlari` o bağlamın adımlarını verir. Üstteki "?" düğmesi o an açık olanı
 * anlatır; tur açıkken bağlam değişirse (pencere açıldı, sekme değişti, sayfa değişti) tur yeni bağlamın anlatımına geçer.
 *
 * Sayfa anlatımları bir "gezinti sırası" (`AKIS`) oluşturur: Ana Sayfa → Firma Kartı (3 sekme) → Standlar → Talepler → Personel.
 * Bir bağlamın son adımı, sıradaki erişilebilir bağlama giden menü öğesini ya da sekmeyi gösterir ve kullanıcının tıklamasını bekler
 * (tur kendi başına sayfa değiştirmez); sıradaki yoksa "?" düğmesini gösteren kapanış adımı gelir.
 *
 * Bir adımın modülü aktif sette en az `min` seviyesinde değilse adım hiç yer almaz (kenar menüyle aynı kural: personel bir sette
 * Standlar ya da Talepler'i Portal Arayüzleri'nde "Yok" yaparsa menüde de turda da yoktur); `setler` verilmiş bir adım yalnızca o
 * setlerde çıkar (Pasaport karesi yalnızca Yabancı Katılımcı'nın satırında vardır).
 *
 * Açıp kapatma iki katmanlıdır: personelin Kayıt Ayarları'ndaki anahtarı (`kayit.portal_egitim`, herkes için, varsayılan KAPALI;
 * systemSettingsService.getPortalEgitimAcik) ve kullanıcının kendisi (ilk girişte kendiliğinden başlar, bitirince ya da kapatınca
 * tur, "?" ile yeniden açılana kadar kapalı kalır). Bu dosya saftır; Shepherd ve sayfalar Components/Firma/FirmaEgitimi.jsx'tedir.
 * Denenir: egitimTuru.test.js.
 *
 * `hedef`: sayfadaki öğeyi bulan seçiciler, sırayla denenir (ilk görünen kazanır). Hiçbiri bulunmazsa (boş liste, dar ekranda
 * gizli menü, yetkisiz düğme) adım ekranın ortasında, öğesiz gösterilir — tur bu yüzden hiçbir veri durumunda kopmaz.
 * Seçiciler sayfalardaki `data-tour="..."` işaretleridir; bir sayfanın yapısı değişse de tur o işareti taşıdığı sürece çalışır.
 */
import { MODUL, SEVIYE, enAz } from './portalService';
import { BAGLAM } from './egitimBaglam';

/** Turun gösterildiği arayüz seti rolü (portal.aktif.rol). */
export const EGITIM_ROLU = 'KATILIMCI';

export const YABANCI_KATILIMCI_SETI = 'YABANCI_KATILIMCI';

/** Bağlam kimlikleri egitimBaglam.js'tedir (pencereler onları tur servislerini çekmeden içe aktarır); burada da dışa verilir. */
export { BAGLAM };

/** Anlatımı olan pencereler (pencerenin `data-tour-baglam` değeri). Anlatımı olmayan bir pencere açıkken tur kapanır. */
export const PENCERE_BAGLAMLARI = [BAGLAM.ONAY_KVKK, BAGLAM.ONAY_KURALLAR, BAGLAM.PERSONEL_EKLE];

const SAYFA_BAGLAMLARI = {
  '/firma/anasayfa': BAGLAM.ANASAYFA,
  '/firma/standlar': BAGLAM.STANDLAR,
  '/firma/talepler': BAGLAM.TALEPLER,
  '/firma/personel': BAGLAM.PERSONEL,
};
const KART_YOLU = '/firma/firmakart';
/** Firma Kartı'nın anlatımı olan sekmeleri (`data-tour="firmakart-sekme-<anahtar>"`); öbür sekmeler (Fuar Katalog) anlatımsızdır. */
const KART_SEKMELERI = {
  bilgiler: BAGLAM.KART_BILGILER,
  yetkililer: BAGLAM.KART_YETKILILER,
  evraklar: BAGLAM.KART_EVRAKLAR,
};

/** `baglamBul` henüz karar veremiyor (onay durumu okunuyor, Firma Kartı sekmeleri çizilmedi): tur olduğu gibi kalır. */
export const BELIRSIZ = 'belirsiz';

/** Onay geçidi şu an ekranda mı (OnayContext.durum 'bekliyor'). */
export const onayEkranindaMi = (onayDurumu) => onayDurumu === 'bekliyor';

/** Bağlam onay geçidinin ya da onun pencerelerinin mi: portal turuyla ayrı "gördü" işareti taşır, geçit kalkınca kapanır. */
export const onayBaglamiMi = (baglam) => typeof baglam === 'string' && baglam.startsWith('onay.');

/**
 * Ekran durumundan bağlamı bulur.
 *  - modalAcik / modal: en üstteki açık pencere ve onun `data-tour-baglam` değeri (işaretsiz pencere anlatımsızdır).
 *  - onayDurumu: OnayContext.durum; pathname: bulunulan adres; sekme: Firma Kartı'nın etkin sekmesi (yoksa null).
 * @returns bağlam kimliği; null = bu ekranın anlatımı yok; BELIRSIZ = henüz bilinmiyor
 */
export function baglamBul({ onayDurumu, pathname, modalAcik, modal, sekme }) {
  if (modalAcik) return PENCERE_BAGLAMLARI.includes(modal) ? modal : null;
  if (onayEkranindaMi(onayDurumu)) return BAGLAM.ONAY;
  if (onayDurumu === 'yukleniyor') return BELIRSIZ;
  if (onayDurumu === 'hata') return null;
  const yol = String(pathname || '').toLowerCase();
  if (yol === KART_YOLU) return sekme == null ? BELIRSIZ : (KART_SEKMELERI[sekme] ?? null);
  return SAYFA_BAGLAMLARI[yol] ?? null;
}

const FIRMA_KART = '/Firma/FirmaKart';
const menuBagi = (yol) => `#navbar-nav a[href="${yol}"]`;
const MENU_DUGMESI = '#topnav-hamburger-icon';

/**
 * Adımlar. `id` aynı zamanda `portal:egitim.adimlar.<id>.baslik|metin` anahtarıdır.
 *  - baglam: adımın ait olduğu bağlam (BAGLAM). Bağlamın adımları bu listedeki sırayla gelir.
 *  - modul / min: adımın ait olduğu portal modülü ve gereken en düşük seviye (modul null = her zaman).
 *  - yer: balonun hedefe göre yeri; liste verilirse bulunan seçicinin sırasındaki (yedek hedefin kendi yeri olabilir).
 *  - setler: verilirse adım yalnızca bu arayüz seti kodlarında (portal.aktif.kod) çıkar.
 *  - satir: adım ekrandaki bir öğeyi (onay satırı, pencerenin bölümü) anlatır; o öğe ekranda yoksa adım turda yer almaz.
 * Metinler `portal:egitim.adimlar.<id>`'de; çoğu Bilge'nin "Tutorial Özeti" tablosundan (2026-10-30), tabloda metni olmayan adımlar
 * (firmaYetkilisi, firmaIsgEvraklari, firmaEvraklari ve pencere adımları: kvkk*, kurallar*, personelEkle*) taslaktır.
 */
export const EGITIM_ADIMLARI = [
  // Onay geçidi: iki satır; satıra tıklamak pencereyi açar ve tur o pencerenin anlatımına geçer. Onaylanmış satır anlatılmaz.
  {
    id: 'onayKvkk', baglam: BAGLAM.ONAY, hedef: ['[data-tour="onay-kvkk"]:not([data-onaylandi])'], yer: 'bottom', satir: true,
  },
  {
    id: 'onayKurallar', baglam: BAGLAM.ONAY, hedef: ['[data-tour="onay-kurallar"]:not([data-onaylandi])'], yer: 'bottom', satir: true,
  },
  { id: 'onayBitis', baglam: BAGLAM.ONAY, hedef: ['[data-tour="egitim-dugmesi"]'], yer: 'bottom' },

  // Onay pencereleri: KVKK Onamı ve Fuar Kuralları (Components/Common/OnayMetniPencereleri.jsx).
  { id: 'kvkkMetin', baglam: BAGLAM.ONAY_KVKK, hedef: ['[data-tour="onay-pencere-metin"]'], yer: 'left' },
  { id: 'kvkkOnay', baglam: BAGLAM.ONAY_KVKK, hedef: ['[data-tour="onay-pencere-onayla"]'], yer: 'top' },
  {
    id: 'kurallarBolumler', baglam: BAGLAM.ONAY_KURALLAR, hedef: ['[data-tour="onay-pencere-bolumler"]'], yer: 'right', satir: true,
  },
  { id: 'kurallarMetin', baglam: BAGLAM.ONAY_KURALLAR, hedef: ['[data-tour="onay-pencere-metin"]'], yer: 'left' },
  {
    id: 'kurallarNot', baglam: BAGLAM.ONAY_KURALLAR, hedef: ['[data-tour="onay-pencere-not"]'], yer: 'top', satir: true,
  },
  { id: 'kurallarOnay', baglam: BAGLAM.ONAY_KURALLAR, hedef: ['[data-tour="onay-pencere-onayla"]'], yer: 'top' },

  // Ana Sayfa: karşılama, menü, Firma Kartı özeti, içindeki iki belge kısayolu, Yüklemeler, İletişim.
  { id: 'hosgeldin', baglam: BAGLAM.ANASAYFA, hedef: [], modul: null },
  {
    id: 'menu', baglam: BAGLAM.ANASAYFA, hedef: ['#navbar-nav', MENU_DUGMESI], yer: ['right', 'bottom'], modul: null,
  },
  {
    id: 'anasayfaFirmaKarti', baglam: BAGLAM.ANASAYFA, hedef: ['[data-tour="anasayfa-firma-karti"]', '[data-tour="anasayfa-ozet"]'],
    yer: ['right', 'bottom'], modul: null,
  },
  {
    id: 'anasayfaBelgeler', baglam: BAGLAM.ANASAYFA, hedef: ['[data-tour="anasayfa-belge-kisayollari"]', '[data-tour="anasayfa-firma-karti"]'],
    yer: ['right', 'right'], modul: null,
  },
  { id: 'anasayfaYuklemeler', baglam: BAGLAM.ANASAYFA, hedef: ['[data-tour="anasayfa-yuklemeler"]'], yer: 'bottom', modul: null },
  { id: 'anasayfaIletisim', baglam: BAGLAM.ANASAYFA, hedef: ['[data-tour="anasayfa-iletisim"]'], yer: 'left', modul: null },

  // Firma Kartı: üç sekme, her biri kendi bağlamı (1 bilgiler · 2 yetkililer · 3 evraklar).
  {
    id: 'firmaKarti', baglam: BAGLAM.KART_BILGILER, hedef: ['[data-tour="firmakart-sekmeler"]'], yer: 'bottom',
    modul: MODUL.FIRMA_BELGELERI, min: SEVIYE.READ,
  },
  {
    id: 'firmaProfili', baglam: BAGLAM.KART_BILGILER, hedef: ['[data-tour="firmakart-profil"]'], yer: 'bottom',
    modul: MODUL.FIRMA_BELGELERI, min: SEVIYE.READ,
  },
  {
    id: 'firmaFaaliyet', baglam: BAGLAM.KART_BILGILER, hedef: ['[data-tour="firmakart-faaliyet"]'], yer: 'bottom',
    modul: MODUL.FIRMA_BELGELERI, min: SEVIYE.READ,
  },
  {
    id: 'firmaFotograflar', baglam: BAGLAM.KART_BILGILER, hedef: ['[data-tour="firmakart-fotograflar"]'], yer: 'bottom',
    modul: MODUL.FIRMA_BELGELERI, min: SEVIYE.READ,
  },
  {
    id: 'firmaKonum', baglam: BAGLAM.KART_BILGILER, hedef: ['[data-tour="firmakart-konum"]'], yer: 'top',
    modul: MODUL.FIRMA_BELGELERI, min: SEVIYE.READ,
  },
  {
    id: 'firmaYetkilisi', baglam: BAGLAM.KART_YETKILILER, hedef: ['[data-tour="firmakart-yetkili"]'], yer: 'bottom',
    modul: MODUL.FIRMA_BELGELERI, min: SEVIYE.READ,
  },
  {
    id: 'firmaKullanicilar', baglam: BAGLAM.KART_YETKILILER, hedef: ['[data-tour="firmakart-kullanicilar"]'], yer: 'bottom',
    modul: MODUL.FIRMA_BELGELERI, min: SEVIYE.READ,
  },
  {
    id: 'firmaGorusme', baglam: BAGLAM.KART_YETKILILER, hedef: ['[data-tour="firmakart-gorusme"]'], yer: 'top',
    modul: MODUL.FIRMA_BELGELERI, min: SEVIYE.READ,
  },
  {
    id: 'firmaIsgEvraklari', baglam: BAGLAM.KART_EVRAKLAR, hedef: ['[data-tour="firmakart-evrak-isg"]'], yer: 'bottom',
    modul: MODUL.FIRMA_BELGELERI, min: SEVIYE.READ,
  },
  {
    id: 'firmaEvraklari', baglam: BAGLAM.KART_EVRAKLAR, hedef: ['[data-tour="firmakart-evrak-firma"]'], yer: 'top',
    modul: MODUL.FIRMA_BELGELERI, min: SEVIYE.READ,
  },

  {
    id: 'standlar', baglam: BAGLAM.STANDLAR, hedef: ['[data-tour="stand-karti"]', '[data-tour="standlar-arama"]'], yer: 'top',
    modul: MODUL.STANDLAR, min: SEVIYE.READ,
  },
  {
    id: 'talepler', baglam: BAGLAM.TALEPLER, hedef: ['[data-tour="talep-karti"]', '[data-tour="talepler-arama"]'], yer: 'top',
    modul: MODUL.TALEPLER, min: SEVIYE.READ,
  },

  // Personel: üstteki Ekle düğmesi, sonra ilk satırdaki kare ve düğmeler. Satırı olmayan (boş liste) bir firmada adım ortada gösterilir.
  {
    id: 'personelEkle', baglam: BAGLAM.PERSONEL, hedef: ['[data-tour="personel-ekle"]', '[data-tour="personel-liste"]'], yer: 'bottom',
    modul: MODUL.PERSONEL, min: SEVIYE.WRITE,
  },
  {
    id: 'personelGorevlendir', baglam: BAGLAM.PERSONEL, hedef: ['[data-tour="personel-gorevlendir"]', '[data-tour="personel-gorev-kaldir"]'],
    yer: 'top', modul: MODUL.PERSONEL, min: SEVIYE.WRITE,
  },
  {
    id: 'personelFoto', baglam: BAGLAM.PERSONEL, hedef: ['[data-tour="personel-foto"]'], yer: 'right', modul: MODUL.PERSONEL, min: SEVIYE.WRITE,
  },
  // #704: the GERÇEK set code (FirmaEgitimi passes portal.aktif.kod, not sadePanel.gorunumSetKodu): the Pasaport square is a document, drawn
  // only for the real Yabancı Katılımcı; a Yerli company in Sade panel modu has its own squares in that slot.
  {
    id: 'personelPasaport', baglam: BAGLAM.PERSONEL, hedef: ['[data-tour="personel-pasaport"]'], yer: 'top',
    modul: MODUL.PERSONEL, min: SEVIYE.WRITE, setler: [YABANCI_KATILIMCI_SETI],
  },
  { id: 'personelKvkk', baglam: BAGLAM.PERSONEL, hedef: ['[data-tour="personel-kvkk"]'], yer: 'top', modul: MODUL.PERSONEL, min: SEVIYE.READ },
  { id: 'personelKkd', baglam: BAGLAM.PERSONEL, hedef: ['[data-tour="personel-kkd"]'], yer: 'top', modul: MODUL.PERSONEL, min: SEVIYE.READ },
  {
    id: 'personelYetki', baglam: BAGLAM.PERSONEL, hedef: ['[data-tour="personel-yetki"]'], yer: 'right', modul: MODUL.PERSONEL, min: SEVIYE.READ,
  },

  // Personel Ekle penceresi (Components/Kisi/PersonelEkleModal.jsx). Kişi zaten bulunduysa sorgu kutusu yoktur, adım düşer.
  {
    id: 'personelEkleSorgu', baglam: BAGLAM.PERSONEL_EKLE, hedef: ['[data-tour="personel-ekle-sorgu"]'], yer: 'bottom', satir: true,
  },
  { id: 'personelEkleKaydet', baglam: BAGLAM.PERSONEL_EKLE, hedef: ['[data-tour="personel-ekle-kaydet"]'], yer: 'top' },
];

/**
 * Gezinti sırası: sayfa anlatımları bu sırayla birbirine bağlanır. `ulasim`: o bağlama NASIL gidilir; bir önceki bağlamın son
 * adımı (sıradaki erişilebilir bağlam için) bu menü öğesini ya da sekmeyi gösterir ve tıklanmasını bekler. `route`: hedef öğe
 * görünmüyorsa (dar ekranda gizli menü) "Oraya git" düğmesinin gideceği adres.
 */
const AKIS = [
  { baglam: BAGLAM.ANASAYFA },
  {
    baglam: BAGLAM.KART_BILGILER,
    ulasim: { id: 'siradakiKart', hedef: [menuBagi(FIRMA_KART), MENU_DUGMESI], yer: ['right', 'bottom'], route: FIRMA_KART },
  },
  {
    baglam: BAGLAM.KART_YETKILILER,
    ulasim: { id: 'siradakiYetkililer', hedef: ['[data-tour="firmakart-sekme-yetkililer"]'], yer: 'bottom' },
  },
  {
    baglam: BAGLAM.KART_EVRAKLAR,
    ulasim: { id: 'siradakiEvraklar', hedef: ['[data-tour="firmakart-sekme-evraklar"]'], yer: 'bottom' },
  },
  {
    baglam: BAGLAM.STANDLAR,
    ulasim: { id: 'siradakiStandlar', hedef: [menuBagi('/Firma/Standlar'), MENU_DUGMESI], yer: ['right', 'bottom'], route: '/Firma/Standlar' },
  },
  {
    baglam: BAGLAM.TALEPLER,
    ulasim: { id: 'siradakiTalepler', hedef: [menuBagi('/Firma/Talepler'), MENU_DUGMESI], yer: ['right', 'bottom'], route: '/Firma/Talepler' },
  },
  {
    baglam: BAGLAM.PERSONEL,
    ulasim: { id: 'siradakiPersonel', hedef: [menuBagi('/Firma/Personel'), MENU_DUGMESI], yer: ['right', 'bottom'], route: '/Firma/Personel' },
  },
];

/** Sayfa anlatımının son adımı, sıradaki bağlam yoksa: "?" düğmesini gösterir. */
const KAPANIS_ADIMI = { id: 'bitis', hedef: ['[data-tour="egitim-dugmesi"]'], yer: 'bottom' };

/** Bağlamın anlatımı olmadığında "?" ile gösterilen tek adım (ekranın ortasında). */
export const YOK_ADIMI = { id: 'yok', hedef: [] };

const adimUygunMu = (a, seviye, setKodu) => (a.modul == null || enAz(seviye(a.modul), a.min ?? SEVIYE.READ))
  && (!a.setler || a.setler.includes(setKodu));

/**
 * Bir bağlamın adımları. `seviye`: portal.seviye gibi (modül) => NONE|READ|WRITE|MANAGE; `setKodu`: portal.aktif.kod (`setler`
 * taşıyan adımlar için; verilmezse o adımlar çıkmaz); `ekrandaVar`: seçici => o öğe şu an ekranda mı (`satir` adımları için;
 * verilmezse hepsi ekrandadır). Sayfa anlatımlarının sonuna sıradaki bağlama giden adım (ya da kapanış adımı) eklenir.
 * Anlatımı olmayan bağlam için boş liste döner.
 */
export function egitimAdimlari(baglam, seviye, setKodu, ekrandaVar = () => true) {
  const icerik = (b) => EGITIM_ADIMLARI.filter((a) => a.baglam === b && adimUygunMu(a, seviye, setKodu)
    && (!a.satir || a.hedef.some(ekrandaVar)));
  const adimlar = icerik(baglam);
  const i = AKIS.findIndex((e) => e.baglam === baglam);
  if (i < 0 || adimlar.length === 0) return adimlar;
  const sonraki = AKIS.slice(i + 1).find((e) => icerik(e.baglam).length > 0);
  return [...adimlar, sonraki ? { ...sonraki.ulasim, baglam, siradaki: sonraki.baglam } : { ...KAPANIS_ADIMI, baglam }];
}

/**
 * Aktif portal bağlamı bu turu görebilir mi: hazır, gerçek bir firma girişi (önizleme değil) ve Katılımcı seti.
 * #704: Sade panel modu bunu değiştirmez (set ROLÜNE bakar, rol firmanın kendisi kalır): turu hiç görmeyen setler (Dekoratör, Organizatör,
 * Taşeron) anahtar AÇIKKEN de görmez; Katılımcılar (Yerli ve Yabancı) görmeye devam eder.
 */
export function egitimGorebilir(portal) {
  return Boolean(portal && portal.hazir && !portal.onizleme && portal.aktif?.rol === EGITIM_ROLU);
}

/** Adım için başka bir sayfaya gidilmeli mi: rota verilmiş ve bulunulan yol farklıysa (sorgu karşılaştırılmaz). */
export function sayfayaGidilmeli(route, pathname) {
  if (!route) return false;
  return route.split('?')[0].toLowerCase() !== String(pathname || '').toLowerCase();
}

/**
 * Turun "gördü" işareti kullanıcıya göre saklanır; aynı tarayıcıda iki giriş birbirininkini bozmaz. Onay ekranı turunun işareti
 * ayrıdır (`tur` = 'onay'): portal turunu görmüş biri onay ekranını artık görmez, ama onay ekranı turunu görmek portal turunu kapatmaz.
 */
export const egitimAnahtari = (kullaniciId, tur) => `fdsEgitim:${kullaniciId ?? 'anonim'}${tur ? `:${tur}` : ''}`;

/** Onay ekranı turunun `tur` değeri (egitimAnahtari). */
export const TUR_ONAY = 'onay';

/** `depo`: localStorage benzeri (okunamazsa tur "görülmemiş" sayılır, yani kendiliğinden başlayabilir). */
export function egitimGoruldu(kullaniciId, depo, tur) {
  try {
    return depo.getItem(egitimAnahtari(kullaniciId, tur)) === '1';
  } catch {
    return false;
  }
}

export function egitimGoruldusuYaz(kullaniciId, depo, tur) {
  try {
    depo.setItem(egitimAnahtari(kullaniciId, tur), '1');
  } catch {
    /* saklanamazsa yalnızca bu oturumda geçerli */
  }
}

/** Tur kendiliğinden başlar mı: anahtar açık, gerçek Katılımcı girişi, Ana Sayfa'dayız ve bu kullanıcı turu henüz görmedi. */
export function egitimKendiligindenBaslar({ anahtarAcik, portal, pathname, goruldu }) {
  return Boolean(anahtarAcik) && egitimGorebilir(portal) && !goruldu
    && String(pathname || '').toLowerCase() === '/firma/anasayfa';
}

/** Onay ekranı turu kendiliğinden başlar mı: anahtar açık, gerçek Katılımcı girişi, onay geçidi ekranda ve bu kullanıcı onay turunu görmedi. */
export function onayTuruKendiligindenBaslar({ anahtarAcik, portal, onayDurumu, goruldu }) {
  return Boolean(anahtarAcik) && egitimGorebilir(portal) && !goruldu && onayEkranindaMi(onayDurumu);
}
